import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Form as AntdForm, Switch, Tag } from 'antd';
import { required } from '../../../../antd/form/FormValidation';
import PhdFormBuilder from '../PhdFormBuilder';
import PhdHelpHint from '../../../../common/Phd/PhdHelpHint.jsx';
import { isStudentActiveInSchoolYear } from '../utils/isStudentActiveInSchoolYear';
import { createSchoolYearChangeHandler } from '../utils/usePhdSchoolYearOptions';

const PhdThesesEditForm = ({
  isAdmin = false,
  studentDisplayNameMap,
  students,
  schoolYears,
  schoolYearOptions,
  ensureSchoolYearSelection,
  isCreatingSchoolYear,
  studyFieldOptions,
  studyFields,
  requiredStudySubjectOptions,
  electiveStudySubjectOptions,
  examinerOptions,
  isStudentHidden,
  isSupervisorHidden,
  isSpecialistHidden,
  isApproved,
  isSupervisorLocked,
  isSubmitting,
  isSubmitDisabled,
  isReadOnly = false,
  currentThesisId,
  initialStudentId,
}) => {
  const { t } = useTranslation();
  const form = AntdForm.useFormInstance();
  const selectedSchoolYearId = AntdForm.useWatch('phd_school_year_id');
  const selectedStudyFieldId = AntdForm.useWatch('phd_study_field_id');
  const selectedStudentId = AntdForm.useWatch('phd_student_id');
  const selectedElectiveSubjectIds =
    AntdForm.useWatch('elective_subject_ids') || [];
  const normalizedStudyFieldId = selectedStudyFieldId
    ? Number(selectedStudyFieldId)
    : null;
  const normalizedSchoolYearId = selectedSchoolYearId
    ? Number(selectedSchoolYearId)
    : null;
  const handleSchoolYearChange = createSchoolYearChangeHandler(
    form,
    ensureSchoolYearSelection,
  );
  const lockNonStudentFields = Boolean(isApproved);
  const completionTypeLabel = useCallback(
    (completionType) => {
      const labels = {
        S: t('Skúška'),
        T: t('Štátna skúška'),
        V: t('Absolvovanie'),
      };

      return labels[completionType] || completionType;
    },
    [t],
  );
  const withCompletionTypeLabel = useCallback(
    (option) => ({
      ...option,
      label: `${option.name || option.label} · ${completionTypeLabel(option.completionType)}`,
    }),
    [completionTypeLabel],
  );

  const filteredStudentOptions = useMemo(() => {
    if (!isAdmin && !normalizedStudyFieldId) {
      return [];
    }

    const activeStudents = (students || [])
      .filter(
        (student) =>
          isAdmin ||
          Number(student.phd_study_field_id) === normalizedStudyFieldId,
      )
      .filter((student) =>
        isStudentActiveInSchoolYear(
          student,
          schoolYears,
          normalizedSchoolYearId,
        ),
      )
      .filter((student) => {
        if (!normalizedSchoolYearId) {
          return true;
        }
        return !(student.theses || []).some(
          (thesis) =>
            Number(thesis.phd_school_year_id) === normalizedSchoolYearId &&
            thesis.id !== currentThesisId,
        );
      });

    if (
      selectedStudentId &&
      !activeStudents.some((student) => student.id === selectedStudentId)
    ) {
      const selectedStudent = (students || []).find(
        (student) => student.id === selectedStudentId,
      );
      if (selectedStudent) {
        activeStudents.push(selectedStudent);
      }
    }

    if (
      initialStudentId &&
      !activeStudents.some((student) => student.id === initialStudentId)
    ) {
      const initialStudent = (students || []).find(
        (student) => student.id === initialStudentId,
      );
      const hasOtherThesisInYear =
        Boolean(normalizedSchoolYearId) &&
        (initialStudent?.theses || []).some(
          (thesis) =>
            Number(thesis.phd_school_year_id) === normalizedSchoolYearId &&
            thesis.id !== currentThesisId,
        );
      if (initialStudent && !hasOtherThesisInYear) {
        activeStudents.push(initialStudent);
      }
    }

    const resolveLabel = (student) => {
      const fallbackName = [
        student?.person?.first_name,
        student?.person?.last_name,
      ]
        .filter(Boolean)
        .join(' ');
      return (
        student?.person?.full_name ||
        student?.person?.display_name ||
        fallbackName ||
        studentDisplayNameMap.get(student.id) ||
        `${student.id}`
      );
    };

    return activeStudents.map((student) => ({
      value: student.id,
      label: resolveLabel(student),
    }));
  }, [
    isAdmin,
    normalizedSchoolYearId,
    normalizedStudyFieldId,
    selectedStudentId,
    studentDisplayNameMap,
    students,
    schoolYears,
    currentThesisId,
    initialStudentId,
  ]);

  const studyFormMessage = t(
    'Vyberte aspoň jednu formu štúdia (dennú alebo externú).',
  );
  const studyFormExtra = (
    <span>
      <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>
      {studyFormMessage}
    </span>
  );
  const researchTypeOptions = useMemo(
    () => [
      {
        value: 'aplikovaný výskum a experimentálny vývoj',
        label: t('aplikovaný výskum a experimentálny vývoj'),
      },
      {
        value: 'aplikovaný výskum',
        label: t('aplikovaný výskum'),
      },
      {
        value: 'základný výskum',
        label: t('základný výskum'),
      },
    ],
    [t],
  );
  const validateStudyForm = () => {
    const isDaily = form?.getFieldValue('is_daily_study');
    const isExternal = form?.getFieldValue('is_external_study');

    if (isDaily || isExternal) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(studyFormMessage));
  };
  const filteredStudySubjectOptions = useMemo(() => {
    const fallbackOptions = [
      ...(requiredStudySubjectOptions || []),
      ...(electiveStudySubjectOptions || []),
    ];
    if (!normalizedStudyFieldId) return [];

    const selectedField = (studyFields || []).find(
      (field) => Number(field.id) === normalizedStudyFieldId,
    );
    const fieldSubjectIds = new Set(
      (selectedField?.subjects || []).map((subject) => Number(subject.id)),
    );
    return fallbackOptions.filter((option) =>
      fieldSubjectIds.has(Number(option.value)),
    );
  }, [
    normalizedStudyFieldId,
    studyFields,
    requiredStudySubjectOptions,
    electiveStudySubjectOptions,
  ]);
  const requiredSubjectOptions = useMemo(
    () =>
      filteredStudySubjectOptions
        .filter((option) => option.completionType === 'T')
        .map((option) => ({
          ...option,
          label: option.name || option.label,
        })),
    [filteredStudySubjectOptions],
  );
  const electiveSubjectOptions = useMemo(
    () =>
      filteredStudySubjectOptions
        .filter((option) => ['S', 'V'].includes(option.completionType))
        .map(withCompletionTypeLabel),
    [filteredStudySubjectOptions, withCompletionTypeLabel],
  );
  useEffect(() => {
    const validElectiveIds = new Set(
      electiveSubjectOptions.map((option) => option.value),
    );
    const nextIds = selectedElectiveSubjectIds.filter((id) =>
      validElectiveIds.has(id),
    );
    if (nextIds.length !== selectedElectiveSubjectIds.length) {
      form.setFieldValue('elective_subject_ids', nextIds);
    }
  }, [electiveSubjectOptions, form, selectedElectiveSubjectIds]);
  const sections = useMemo(() => {
    const basicFields = [
      isStudentHidden
        ? null
        : {
            name: 'phd_student_id',
            label: t('Študent'),
            type: 'select',
            options: filteredStudentOptions,
            componentProps: {
              disabled: false,
              extra: t(
                'Zoznam študentov zobrazuje iba aktívnych pre vybraný školský rok. Študenta môžete priradiť aj neskôr.',
              ),
            },
            colSpan: 12,
          },
      {
        name: 'phd_school_year_id',
        label: (
          <span>
            {t('Školský rok')}
            <PhdHelpHint
              label={t('Čo znamená školský rok témy?')}
              title={t(
                'Rok označuje začiatok účinnosti témy. Téma platí aj v ďalších rokoch až do náhrady alebo obhajoby.',
              )}
            />
          </span>
        ),
        type: 'select',
        options: schoolYearOptions,
        componentProps: {
          onChange: handleSchoolYearChange,
          loading: isCreatingSchoolYear,
          disabled: lockNonStudentFields,
        },
        rules: [required(t)],
        colSpan: 12,
      },
      {
        name: 'phd_study_field_id',
        label: t('Študijný odbor'),
        type: 'select',
        options: studyFieldOptions,
        rules: [required(t)],
        componentProps: {
          disabled: lockNonStudentFields,
        },
        colSpan: 12,
      },
      isSupervisorHidden
        ? null
        : {
            name: 'phd_supervisor_id',
            label: t('Školiteľ'),
            type: 'select',
            options: examinerOptions,
            rules: [required(t)],
            componentProps: {
              disabled: isSupervisorLocked || lockNonStudentFields,
            },
            colSpan: 12,
          },
      isSpecialistHidden
        ? null
        : {
            name: 'phd_supervisor_specialist_id',
            label: t('Špecialista'),
            type: 'select',
            options: examinerOptions,
            componentProps: {
              disabled: lockNonStudentFields,
            },
            colSpan: 12,
          },
      {
        name: 'title_sk',
        label: t('Názov (SK)'),
        type: 'text',
        rules: [required(t)],
        componentProps: {
          disabled: lockNonStudentFields,
        },
        colSpan: 12,
      },
      {
        name: 'title_en',
        label: t('Názov (EN)'),
        type: 'text',
        componentProps: {
          disabled: lockNonStudentFields,
        },
        colSpan: 12,
      },
      {
        key: 'is_daily_study',
        colSpan: 6,
        render: () => (
          <AntdForm.Item
            label={t('Denné štúdium')}
            name="is_daily_study"
            valuePropName="checked"
            dependencies={['is_external_study']}
            extra={studyFormExtra}
            rules={[{ validator: validateStudyForm }]}
          >
            <Switch
              checkedChildren={t('Áno')}
              unCheckedChildren={t('Nie')}
              disabled={lockNonStudentFields}
            />
          </AntdForm.Item>
        ),
      },
      {
        key: 'is_external_study',
        colSpan: 6,
        render: () => (
          <AntdForm.Item
            label={t('Externé štúdium')}
            name="is_external_study"
            valuePropName="checked"
            dependencies={['is_daily_study']}
            rules={[{ validator: validateStudyForm }]}
          >
            <Switch
              checkedChildren={t('Áno')}
              unCheckedChildren={t('Nie')}
              disabled={lockNonStudentFields}
            />
          </AntdForm.Item>
        ),
      },
    ].filter(Boolean);

    return [
      {
        key: 'basic',
        title: t('Základné informácie'),
        fields: basicFields,
      },
      {
        key: 'required-subjects',
        title: t('Študijné jednotky'),
        fields: [
          {
            key: 'required-subjects-list',
            colSpan: 24,
            render: () => (
              <AntdForm.Item label={t('Povinné študijné jednotky')}>
                {requiredSubjectOptions.length
                  ? requiredSubjectOptions.map((option) => (
                      <Tag key={option.value}>{option.label}</Tag>
                    ))
                  : '—'}
              </AntdForm.Item>
            ),
          },
          {
            name: 'elective_subject_ids',
            label: t('Výberové študijné jednotky'),
            type: 'select',
            options: electiveSubjectOptions,
            componentProps: {
              mode: 'multiple',
              allowClear: true,
              disabled: lockNonStudentFields,
            },
            colSpan: 24,
          },
        ],
      },
      {
        key: 'content',
        title: t('Obsah práce'),
        fields: [
          {
            name: 'description_sk',
            label: t('Detailnejší opis problému (SK)'),
            type: 'editor',
            colSpan: 24,
            rules: [required(t)],
            componentProps: {
              imgUploadDirectory: 'phd_topics',
              fileUploadDirectory: 'phd_topics',
              disabled: lockNonStudentFields,
            },
          },
          {
            name: 'description_en',
            label: t('Detailnejší opis problému (EN)'),
            type: 'editor',
            colSpan: 24,
            componentProps: {
              imgUploadDirectory: 'phd_topics',
              fileUploadDirectory: 'phd_topics',
              disabled: lockNonStudentFields,
            },
          },
          {
            name: 'scientific_contribution',
            label: t(
              'Predpokladaný vedecký prínos (algoritmy, metodológie, ...)',
            ),
            type: 'editor',
            colSpan: 24,
            rules: [required(t)],
            componentProps: {
              imgUploadDirectory: 'phd_topics',
              fileUploadDirectory: 'phd_topics',
              disabled: lockNonStudentFields,
            },
          },
          {
            name: 'scientific_progress',
            label: t('Odporučaný postup vedeckého bádania'),
            type: 'editor',
            colSpan: 24,
            rules: [required(t)],
            componentProps: {
              imgUploadDirectory: 'phd_topics',
              fileUploadDirectory: 'phd_topics',
              disabled: lockNonStudentFields,
            },
          },
          {
            name: 'research_type',
            label: t('Druh výskumu'),
            type: 'select',
            options: researchTypeOptions,
            colSpan: 12,
            rules: [required(t)],
            componentProps: {
              disabled: lockNonStudentFields,
            },
          },
          {
            name: 'research_task',
            label: t('Výskumná úloha, ktorej súčasťou bude riešená téma'),
            type: 'editor',
            colSpan: 24,
            rules: [required(t)],
            componentProps: {
              imgUploadDirectory: 'phd_topics',
              fileUploadDirectory: 'phd_topics',
              disabled: lockNonStudentFields,
            },
          },
          {
            name: 'solution_results',
            label: t(
              'Doterajšie výsledky riešenia na školiacom pracovisku, vrátane odkazov na dostupné zdroje',
            ),
            type: 'editor',
            colSpan: 24,
            rules: [required(t)],
            componentProps: {
              imgUploadDirectory: 'phd_topics',
              fileUploadDirectory: 'phd_topics',
              disabled: lockNonStudentFields,
            },
          },
        ],
      },
    ];
  }, [
    t,
    filteredStudentOptions,
    schoolYearOptions,
    studyFieldOptions,
    examinerOptions,
    handleSchoolYearChange,
    isCreatingSchoolYear,
    isStudentHidden,
    isSupervisorHidden,
    isSpecialistHidden,
    isApproved,
    isSupervisorLocked,
    requiredSubjectOptions,
    electiveSubjectOptions,
    researchTypeOptions,
  ]);

  return (
    <PhdFormBuilder
      sections={sections}
      submitKey="phd-theses"
      isSubmitting={isSubmitting}
      isSubmitDisabled={isSubmitDisabled}
      hideSubmit={isReadOnly}
    />
  );
};

export default PhdThesesEditForm;
